import { deg2radLocal } from "./angles";
import { rcFrame, type Pt, type WallInput, type WallResult } from "@tasduvar/engine";
import { polygonEdgeDims } from "./polygonDims";

export interface Box { minX: number; minY: number; maxX: number; maxY: number }
export interface Transform { scale: number; ox: number; oy: number; width: number; height: number }

export function boundsOf(pts: Pt[]): Box {
  return pts.reduce((b, p) => ({ minX: Math.min(b.minX, p.x), minY: Math.min(b.minY, p.y), maxX: Math.max(b.maxX, p.x), maxY: Math.max(b.maxY, p.y) }), { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
}
export const expandBox = (b: Box, pad: number): Box => ({ minX: b.minX - pad, minY: b.minY - pad, maxX: b.maxX + pad, maxY: b.maxY + pad });

export function fitTransform(box: Box, width: number, height: number, margin = 24): Transform {
  const w = Math.max(box.maxX - box.minX, 1e-6), h = Math.max(box.maxY - box.minY, 1e-6);
  const scale = Math.min((width - 2 * margin) / w, (height - 2 * margin) / h);
  const ox = (width - w * scale) / 2 - box.minX * scale;
  const oy = (height + h * scale) / 2 + box.minY * scale;
  return { scale, ox, oy, width, height };
}
export const toSvg = (t: Transform, p: Pt): { x: number; y: number } => ({ x: p.x * t.scale + t.ox, y: t.oy - p.y * t.scale });
/** SVG piksel → model koordinatı (ters dönüşüm). */
export const fromSvg = (t: Transform, s: { x: number; y: number }): Pt => ({ x: (s.x - t.ox) / t.scale, y: (t.oy - s.y) / t.scale });
const r1 = (v: number): string => String(Math.round(v * 100) / 100);
export function pathOf(t: Transform, pts: Pt[]): string {
  return pts.map((p, i) => { const s = toSvg(t, p); return `${i === 0 ? "M" : "L"} ${r1(s.x)} ${r1(s.y)}`; }).join(" ") + " Z";
}

export function backfillSurface(result: WallResult, wall: WallInput, farX: number): Pt[] {
  const g = result.geometry;
  const top = g.virtualPlane.top;                                   // dolgu yüzeyi ile düzlem kesişimi
  const back = g.polygon.reduce((a, p) => (p.y >= g.yTop - 1e-6 && p.x > a.x ? p : a), { x: -Infinity, y: g.yTop });   // tepe arka köşe
  const tanB = Math.tan(deg2radLocal(wall.backfill.slopeAngle));
  return [back, top, { x: farX, y: top.y + (farX - top.x) * tanB }];
}

/**
 * `extra`: sahneye alınması istenen ek noktalar (ör. toptan göçme dairesinin giriş/çıkış/en alt noktası); arka uzaklık da onlara göre uzar.
 * `labelWidthM`: önde "H = x,xx m" etiketi için ayrılacak genişlik (m). Verilmezse 0,35·H + 0,6 kabulü; çağıran ilk sığdırmadan
 * sonra piksel genişliğini ölçeğe bölerek ikinci geçişte gerçek değeri verebilir (sahne yatayda gereksiz yayvan olmasın).
 */
export function sceneBox(result: WallResult, wall: WallInput, extra: Pt[] = [], labelWidthM?: number): Box {
  const g = result.geometry;
  // Arkada kama + biraz dolgu (≈ H kadar), önde ≈ 0,4·H zemin; sahne gereksiz yere yayvan olmasın
  const farX = Math.max(g.B + g.yTop, g.virtualPlane.top.x + 0.5 * g.yTop, g.B + 2, ...extra.map((p) => p.x + 0.5));
  const surf = backfillSurface(result, wall, farX);
  // Ön zemin taban kotunun Df kadar ÜSTÜNDE (gömme derinliği); sahne alt sınırı tabanın 0,5·max(1, 0,3·H) altı
  const below = Math.min(-Math.max(1, 0.3 * g.yTop) * 0.5 - 0.5, ...extra.map((p) => p.y - 0.5));
  // Önde H / Df / kademe ölçü çizgileri için yer (en dış ofset + "H = x,xx m" etiketi, ölçekle büyür); toptan göçme dairesi varsa ona da yer
  const labelM = labelWidthM ?? 0.35 * g.yTop + 0.6;
  const frontX = Math.min(-Math.max(g.yTop * 0.4, frontDimOffset(wall) + labelM), ...extra.map((p) => p.x - 0.5));
  const pts: Pt[] = [...g.polygon, ...surf, { x: frontX, y: below }, { x: farX, y: below }, { x: frontX, y: wall.geometry.embedmentDepth }, ...extra];
  if (wall.water.backLevel !== null) pts.push({ x: farX, y: wall.water.backLevel });
  return expandBox(boundsOf(pts), 0.3);
}

/** Sahne kutusu + dönüşüm, iki geçişli: ilk ölçekten "H = x,xx m" etiketinin metre genişliği (≈ 72 px + pay) bulunur, kutu onunla yeniden kurulur. */
export function fitScene(result: WallResult, wall: WallInput, width: number, height: number, margin: number, extra?: Pt[]): { box: Box; t: Transform } {
  const box1 = sceneBox(result, wall, extra);
  const t1 = fitTransform(box1, width, height, margin);
  const box = sceneBox(result, wall, extra, 72 / t1.scale + 0.2);
  return { box, t: fitTransform(box, width, height, margin) };
}

const fmtM = (v: number): string => v.toFixed(2).replace(".", ",");
const fmtRatio = (v: number): string => String(Math.round(v * 10) / 10).replace(".", ",");

export interface DimLine { key: string; a: Pt; b: Pt; label: string; offset: number }
/** Ön tarafta kullanılan en büyük ölçü ofseti (sahne kutusu için). */
export function frontDimOffset(wall: WallInput): number {
  const Df = wall.geometry.embedmentDepth > 0;
  const multi = wall.geometry.mode !== "parametric" || wall.geometry.steps.length > 1 || !!wall.geometry.foundation;   // çokgen: kenar ölçüleri önde yer tutar
  return Df ? (multi ? 2.5 : 1.7) : multi ? 1.7 : 0.9;
}
/**
 * Ölçü çizgileri. Anahtarlar `applyDimension` ve geometri alanlarının `dimKey`'iyle eşleşir:
 * H, B, top(=s0.t), Df, s{i}.h / s{i}.t / s{i}.b (kademe), f.h / f.toe / f.heel (temel). Çokgen modunda H, B, Df + kenar ölçüleri e{i}.x / e{i}.y.
 */
export function dimensionLines(result: WallResult, wall: WallInput): DimLine[] {
  const g = result.geometry;
  const geo = wall.geometry;
  const Df = geo.embedmentDepth;
  const dims: DimLine[] = [];
  const multi = geo.mode !== "parametric" || geo.steps.length > 1 || !!geo.foundation;
  const hOff = Df > 0 ? (multi ? -2.5 : -1.7) : multi ? -1.7 : -0.9;
  dims.push({ key: "H", a: { x: g.toe.x, y: 0 }, b: { x: g.toe.x, y: g.yTop }, label: `H = ${fmtM(g.yTop)} m`, offset: hOff });
  // B ölçüsü taban gerilmesi diyagramının (≈ 0,12·H) altında kalsın; eğik tabanda yatay izdüşüm, topuk (en alt) kotunda
  dims.push({ key: "B", a: { x: g.toe.x, y: g.heel.y }, b: g.heel, label: `B = ${fmtM(g.B)} m`, offset: -(0.12 * g.yTop + (geo.mode === "rc-cantilever" ? 1.0 : 0.9)) });
  if (Df > 0) dims.push({ key: "Df", a: { x: g.toe.x, y: 0 }, b: { x: g.toe.x, y: Df }, label: `Df = ${fmtM(Df)} m`, offset: multi ? -1.7 : -0.9 });
  if (geo.mode === "rc-cantilever") {
    const f = rcFrame(geo);
    dims.push({ key: "rc.tt", a: { x: f.xTopFront, y: f.yTop }, b: { x: f.xTopBack, y: f.yTop }, label: `${fmtM(f.tt)} m`, offset: 0.4 });
    // Taban altında zincir ölçü: burun | gövde alt kalınlığı | topuk (taban gerilmesi diyagramının altında, B ölçüsünün üstünde)
    const chain = -(0.12 * g.yTop + 0.45);
    dims.push({ key: "rc.tb", a: { x: f.xStemFront, y: 0 }, b: { x: f.xStemBack, y: 0 }, label: `${fmtM(f.tb)} m`, offset: chain });
    dims.push({ key: "rc.hs", a: { x: g.toe.x, y: f.tf }, b: { x: g.toe.x, y: f.yTop }, label: `${fmtM(f.hs)} m`, offset: -0.9 });
    dims.push({ key: "rc.tf", a: { x: g.heel.x, y: 0 }, b: { x: g.heel.x, y: f.tf }, label: `${fmtM(f.tf)} m`, offset: 0.6 });
    if (f.toe > 0) dims.push({ key: "rc.toe", a: { x: 0, y: 0 }, b: { x: f.toe, y: 0 }, label: `${fmtM(f.toe)} m`, offset: chain });
    if (f.heel > 0) dims.push({ key: "rc.heel", a: { x: f.xStemBack, y: 0 }, b: { x: f.B, y: 0 }, label: `${fmtM(f.heel)} m`, offset: chain });
    if (geo.key) dims.push({ key: "rc.key", a: { x: geo.key.offsetFromToe + geo.key.width, y: -geo.key.depth }, b: { x: geo.key.offsetFromToe + geo.key.width, y: 0 }, label: `${fmtM(geo.key.depth)} m`, offset: 0.5 });
    return dims;
  }
  if (geo.mode !== "parametric") { dims.push(...polygonEdgeDims(geo.points)); return dims; }
  const hf = geo.foundation?.height ?? 0, toeExt = geo.foundation?.toeExtension ?? 0, heelExt = geo.foundation?.heelExtension ?? 0;
  const mf = geo.frontBatter;
  const Hb = geo.steps.reduce((s, st) => s + st.height, 0);
  let yTop = Hb;
  geo.steps.forEach((st, i) => {
    const yBot = yTop - st.height;
    const xT = toeExt + mf * yTop, xB = toeExt + mf * yBot;
    dims.push({ key: i === 0 ? "top" : `s${i}.t`, a: { x: xT, y: hf + yTop }, b: { x: xT + st.topThickness, y: hf + yTop }, label: `${fmtM(st.topThickness)} m`, offset: i === 0 ? 0.4 : 0.3 });
    // Son kademenin alt kalınlığı temel yoksa B ile aynı; yinelenmesin
    if (i < geo.steps.length - 1 || geo.foundation) dims.push({ key: `s${i}.b`, a: { x: xB, y: hf + yBot }, b: { x: xB + st.bottomThickness, y: hf + yBot }, label: `${fmtM(st.bottomThickness)} m`, offset: -0.3 });
    if (multi) dims.push({ key: `s${i}.h`, a: { x: g.toe.x, y: hf + yBot }, b: { x: g.toe.x, y: hf + yTop }, label: `${fmtM(st.height)} m`, offset: -0.9 });
    yTop = yBot;
  });
  const bs = geo.baseSlope ?? 0;
  if (geo.foundation) {
    // Eğik tabanda temel yüksekliği burunda ölçülür (topukta hf + B·tanα); topuk tarafında taban eğimi ölçüsü var
    dims.push(bs > 0
      ? { key: "f.h", a: { x: g.toe.x, y: 0 }, b: { x: g.toe.x, y: hf }, label: `${fmtM(hf)} m`, offset: -0.35 }
      : { key: "f.h", a: { x: g.heel.x, y: 0 }, b: { x: g.heel.x, y: hf }, label: `${fmtM(hf)} m`, offset: 0.6 });
    if (toeExt > 0) dims.push({ key: "f.toe", a: { x: 0, y: 0 }, b: { x: toeExt, y: 0 }, label: `${fmtM(toeExt)} m`, offset: -0.35 });
    if (heelExt > 0) dims.push({ key: "f.heel", a: { x: g.heel.x - heelExt, y: g.heel.y }, b: { x: g.heel.x, y: g.heel.y }, label: `${fmtM(heelExt)} m`, offset: -0.35 });
  }
  // Eğik taban: topuğun burun kotundan düşüşü Δ = B·tanα (tıklayıp değiştirilebilir → tanα = Δ/B)
  if (bs > 0) dims.push({ key: "bs", a: { x: g.heel.x, y: g.heel.y }, b: { x: g.heel.x, y: g.toe.y }, label: `Δ = ${fmtM(g.toe.y - g.heel.y)} m (1/${fmtRatio(1 / bs)})`, offset: 0.6 });
  return dims;
}
